import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import { refurbishedProducts } from "../../assets/refurbishedProducts";
import emailjs from "@emailjs/browser";
import {
  FaTimes,
  FaCheck,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
} from "react-icons/fa";

const TAB_KEYS = ["features", "specifications", "included", "policies"];
const TAB_LABELS = [
  "Features",
  "Specifications",
  "What's Included",
  "Shipping · Returns · Warranty",
];

const ProductInfo = () => {
  const { model } = useParams();
  const navigate = useNavigate();
  const form = useRef();
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  // Find product by model
  const productInfo = refurbishedProducts.find((p) => p.model === model);

  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState("features");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });

  const images = productInfo?.images || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await emailjs.sendForm(
        "service_ynxsiyf",
        "template_1eph1hj",
        form.current,
        "yI2IoGVhugkTFtnK1",
      );

      if (result.text === "OK") {
        setSubmitted(true);
        setFormData({ fullName: "", email: "", phone: "", message: "" });
        setTimeout(() => {
          setSubmitted(false);
          setQuoteOpen(false);
        }, 2500);
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!productInfo) {
    return (
      <div
        className="error-container"
        style={{ textAlign: "center", padding: "4rem" }}
      >
        <h2>Product not found</h2>
        <button
          onClick={() => navigate(-1)}
          style={{ padding: "0.5rem 1rem", cursor: "pointer" }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const p = productInfo.policies || {};

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: productInfo.name,
    sku: productInfo.model,
    mpn: productInfo.model,
    brand: {
      "@type": "Brand",
      name: productInfo.by || "Medichy",
    },
    description: productInfo.feature?.substring(0, 300),
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/RefurbishedCondition",
    },
    image: productInfo.images?.map((img) =>
      img.startsWith("http") ? img : `${window.location.origin}${img}`,
    ),
  };

  return (
    <>
      <Helmet>
        <title>
          {productInfo.name} | Medichy Refurbished Medical Equipment
        </title>
        <meta
          name="description"
          content={`${productInfo.name} - Model ${productInfo.model}. Certified refurbished medical equipment by ${productInfo.by}. ${productInfo.feature?.substring(0, 150)}`}
        />
        <meta
          name="keywords"
          content={`${productInfo.name}, ${productInfo.model}, refurbished medical equipment, ${productInfo.by}, medical device, hospital equipment`}
        />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${currentUrl}`} />

        {/* Open Graph / Social Media Meta Tags */}
        <meta property="og:title" content={`${productInfo.name} | Medichy`} />
        <meta
          property="og:description"
          content={`Model: ${productInfo.model}. Certified refurbished medical equipment. Request a quote today.`}
        />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={currentUrl} />
        <meta
          property="og:image"
          content={productInfo.images?.[0] || "/medichy-logo.png"}
        />
        <meta property="og:site_name" content="Medichy" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={productInfo.name} />
        <meta
          name="twitter:description"
          content={`Model: ${productInfo.model} - Certified refurbished medical equipment`}
        />
        <meta
          name="twitter:image"
          content={productInfo.images?.[0] || "/medichy-logo.png"}
        />

        {/* Additional SEO Meta Tags */}
        <meta name="author" content="Medichy" />
        <meta name="geo.region" content="US-DE" />
        <meta name="geo.position" content="39.158168;-75.524368" />
        <meta name="ICBM" content="39.158168, -75.524368" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <TheProductInfo>
        {/* BACK BUTTON */}
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back to Products
        </button>

        {/* TOP SECTION: Images + Info Panel */}
        <div className="top-section">
          {/* Left: image viewer */}
          <div className="image-col">
            <div className="main-image-wrap">
              <img
                src={images[selectedImage] || "/placeholder-image.jpg"}
                alt={productInfo.name}
                className="main-image"
                loading="eager"
              />
              <button
                className="expand-btn"
                onClick={() => openLightbox(selectedImage)}
                aria-label="Expand image"
              >
                <FaExpand />
              </button>
            </div>

            {images.length > 1 && (
              <div className="thumbnail-row">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`thumb ${selectedImage === i ? "active" : ""}`}
                    onClick={() => setSelectedImage(i)}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img
                      src={img}
                      alt={`${productInfo.name} - view ${i + 1}`}
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: product info */}
          <div className="info-col">
            <span className="info-tag">
              Certified Refurbished Medical Equipment
            </span>
            <h1 className="product-name">{productInfo.name}</h1>

            <div className="info-table">
              <div className="info-row">
                <span className="info-label">Product Code :</span>
                <span className="info-value mono">{productInfo.model}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Manufacturer</span>
                <span className="info-value">{productInfo.by}</span>
              </div>
              <div className="info-row">
                <span className="info-label">MOQ</span>
                <span className="info-value">
                  {productInfo.moq} Unit{productInfo.moq > 1 ? "s" : ""}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Condition</span>
                <span className="info-value condition-badge">
                  {productInfo.condition}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Shipping</span>
                <span className="info-value">{productInfo.shipping}</span>
              </div>
            </div>

            <button className="quote-btn" onClick={() => setQuoteOpen(true)}>
              Get a Quote
            </button>

            <p className="reassurance">
              Every unit is disassembled, inspected, tested, and cleaned to
              Medichy quality standards before shipment.
            </p>
          </div>
        </div>

        {/* BOTTOM SECTION: Tabbed Detail Table */}
        <div className="detail-section">
          {/* Tab headers */}
          <div className="tab-bar">
            {TAB_KEYS.map((key, i) => (
              <button
                key={key}
                className={`tab-btn ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                {TAB_LABELS[i]}
              </button>
            ))}
          </div>

          {/* Tab panels */}
          <div className="tab-panel">
            {/* FEATURES */}
            {activeTab === "features" && (
              <div className="tab-content">
                <div className="feature-overview">
                  <span className="panel-tag">Overview</span>
                  <p>{productInfo.feature}</p>
                </div>
                {productInfo.keyFeatures?.length > 0 &&
                  productInfo.keyFeatures[0] !== "" && (
                    <div className="key-features">
                      <span className="panel-tag">Key Features</span>
                      <ul>
                        {productInfo.keyFeatures.map(
                          (f, i) =>
                            f &&
                            f !== "" && (
                              <li key={i}>
                                <span className="feat-num">
                                  {String(i + 1).padStart(2, "0")}
                                </span>
                                <span>{f}</span>
                              </li>
                            ),
                        )}
                      </ul>
                    </div>
                  )}
              </div>
            )}

            {/* SPECIFICATIONS */}
            {activeTab === "specifications" && (
              <div className="tab-content">
                {productInfo.specifications?.map(
                  (spec, i) =>
                    spec &&
                    spec.measurements &&
                    spec.measurements.length > 0 && (
                      <div className="spec-block" key={i}>
                        {spec.title && (
                          <h4 className="spec-title">{spec.title}</h4>
                        )}
                        <div className="spec-measurements">
                          {spec.measurements.map(
                            (m, j) =>
                              m &&
                              m !== "" && (
                                <div className="spec-row" key={j}>
                                  <span>{m}</span>
                                </div>
                              ),
                          )}
                        </div>
                      </div>
                    ),
                )}
              </div>
            )}

            {/* WHAT'S INCLUDED */}
            {activeTab === "included" && (
              <div className="tab-content">
                <span className="panel-tag">Included Accessories</span>
                <ul className="included-list">
                  {productInfo.includedAccessories?.map(
                    (item, i) =>
                      item &&
                      item !== "" && (
                        <li key={i}>
                          <span className="check-mark">✓</span>
                          <span>{item}</span>
                        </li>
                      ),
                  )}
                </ul>
              </div>
            )}

            {/* POLICIES */}
            {activeTab === "policies" && (
              <div className="tab-content policies-content">
                {/* Shipping */}
                {p.shippingCharges && (
                  <div className="policy-block">
                    <h4 className="policy-heading">Shipping</h4>
                    {Object.values(p.shippingCharges).map(
                      (val, i) =>
                        val && (
                          <div className="policy-row" key={i}>
                            <span className="policy-dot" />
                            <p>{val}</p>
                          </div>
                        ),
                    )}
                  </div>
                )}

                {/* Return Policy */}
                {p.returnPolicy && (
                  <div className="policy-block">
                    <h4 className="policy-heading">Return Policy</h4>
                    {Object.values(p.returnPolicy).map(
                      (val, i) =>
                        val && (
                          <div className="policy-row" key={i}>
                            <span className="policy-dot" />
                            <p>{val}</p>
                          </div>
                        ),
                    )}
                  </div>
                )}

                {/* Credit & Return Guidelines */}
                {p.creditAndReturnShippingGuidelines && (
                  <div className="policy-block">
                    <h4 className="policy-heading">
                      Credit & Return Guidelines
                    </h4>
                    {Object.values(p.creditAndReturnShippingGuidelines).map(
                      (val, i) =>
                        val && (
                          <div className="policy-row" key={i}>
                            <span className="policy-dot" />
                            <p>{val}</p>
                          </div>
                        ),
                    )}
                  </div>
                )}

                {/* Warranty */}
                {p.warranty && (
                  <div className="policy-block">
                    <h4 className="policy-heading">Warranty</h4>
                    {p.warranty.refurbishedWarranty && (
                      <div className="policy-row">
                        <span className="policy-dot" />
                        <p>{p.warranty.refurbishedWarranty}</p>
                      </div>
                    )}
                    {p.warranty.coverage && (
                      <>
                        {p.warranty.coverage.partsAndLabor && (
                          <div className="policy-row">
                            <span className="policy-dot" />
                            <p>{p.warranty.coverage.partsAndLabor}</p>
                          </div>
                        )}
                        {p.warranty.coverage.extendedWarranty && (
                          <div className="policy-row">
                            <span className="policy-dot" />
                            <p>{p.warranty.coverage.extendedWarranty}</p>
                          </div>
                        )}
                      </>
                    )}
                    {p.warranty.replacementPolicy && (
                      <div className="policy-row">
                        <span className="policy-dot" />
                        <p>{p.warranty.replacementPolicy}</p>
                      </div>
                    )}
                    {p.warranty.nonCoveredIssues?.length > 0 && (
                      <div className="policy-subblock">
                        <span className="panel-tag">Not Covered</span>
                        <div className="inline-tags">
                          {p.warranty.nonCoveredIssues.map((issue, i) => (
                            <span className="policy-tag" key={i}>
                              {issue}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {p.warranty.voidConditions && (
                      <div className="policy-row">
                        <span className="policy-dot" />
                        <p>{p.warranty.voidConditions}</p>
                      </div>
                    )}
                    {p.warranty.warrantyClaimRequirements?.length > 0 && (
                      <div className="policy-subblock">
                        <span className="panel-tag">Claim Requirements</span>
                        {p.warranty.warrantyClaimRequirements.map((req, i) => (
                          <div className="policy-row" key={i}>
                            <span className="policy-dot" />
                            <p>{req}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.warranty.returnShipping && (
                      <div className="policy-row">
                        <span className="policy-dot" />
                        <p>{p.warranty.returnShipping}</p>
                      </div>
                    )}
                    {p.warranty.liabilityLimitation && (
                      <div className="policy-row">
                        <span className="policy-dot" />
                        <p>{p.warranty.liabilityLimitation}</p>
                      </div>
                    )}
                    {p.warranty.governingLaw && (
                      <div className="policy-row">
                        <span className="policy-dot" />
                        <p>{p.warranty.governingLaw}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* QUOTE MODAL */}
        {quoteOpen && (
          <ModalOverlay onClick={() => setQuoteOpen(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <div>
                  <span className="modal-tag">Price Enquiry</span>
                  <h3>{productInfo.name}</h3>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setQuoteOpen(false)}
                >
                  <FaTimes />
                </button>
              </ModalHeader>

              {submitted ? (
                <SuccessMessage>
                  <FaCheck />
                  <h4>Enquiry Sent Successfully!</h4>
                  <p>Our sales team will contact you within 24 hours.</p>
                </SuccessMessage>
              ) : (
                <form
                  ref={form}
                  className="modal-form"
                  onSubmit={handleQuoteSubmit}
                >
                  <input
                    type="hidden"
                    name="product_name"
                    value={productInfo.name}
                  />
                  <input
                    type="hidden"
                    name="product_sku"
                    value={productInfo.model}
                  />

                  <FormGroup>
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email address"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Tell us about your requirements..."
                    />
                  </FormGroup>

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Submit Enquiry"}
                  </SubmitButton>
                </form>
              )}
            </ModalContent>
          </ModalOverlay>
        )}

        {/* LIGHTBOX MODAL */}
        {isLightboxOpen && (
          <LightboxOverlay onClick={closeLightbox}>
            <LightboxContent onClick={(e) => e.stopPropagation()}>
              <LightboxCloseBtn onClick={closeLightbox}>
                <FaTimes />
              </LightboxCloseBtn>
              <LightboxNavBtn className="prev" onClick={prevImage}>
                <FaChevronLeft />
              </LightboxNavBtn>
              <LightboxImage>
                <img
                  src={images[lightboxIndex]}
                  alt={`${productInfo.name} - Full size view`}
                />
              </LightboxImage>
              <LightboxNavBtn className="next" onClick={nextImage}>
                <FaChevronRight />
              </LightboxNavBtn>
              <LightboxCounter>
                {lightboxIndex + 1} / {images.length}
              </LightboxCounter>
            </LightboxContent>
          </LightboxOverlay>
        )}
      </TheProductInfo>
    </>
  );
};

const TheProductInfo = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #faf9f7;
  color: #1c1c1c;
  padding: 2rem 4rem 6rem;

  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu,
    Cantarell, "Open Sans", "Helvetica Neue", sans-serif;

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #666;
    cursor: pointer;
    padding: 0;
    margin-bottom: 2.5rem;
    transition: color 0.2s;

    &:hover {
      color: #1c1c1c;
    }
  }

  .top-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 5rem;
    max-width: 1200px;
    margin: 0 auto 5rem;
    align-items: start;
  }

  .image-col {
    .main-image-wrap {
      position: relative;
      width: 100%;
      aspect-ratio: 4 / 3;
      background: #fff;
      border: 1px solid #e0dbd3;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;

      .main-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 1.5rem;
      }

      .expand-btn {
        position: absolute;
        bottom: 1rem;
        right: 1rem;
        background: rgba(0, 0, 0, 0.6);
        color: white;
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        backdrop-filter: blur(4px);

        &:hover {
          background: var(--primary-color);
          transform: scale(1.05);
        }
      }
    }

    .thumbnail-row {
      display: flex;
      gap: 0.6rem;
      margin-top: 0.8rem;
      flex-wrap: wrap;

      .thumb {
        width: 72px;
        height: 72px;
        border: 1px solid #e0dbd3;
        background: #fff;
        padding: 0;
        cursor: pointer;
        overflow: hidden;
        transition: border-color 0.2s;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 4px;
        }

        &.active,
        &:hover {
          border-color: #1c1c1c;
        }
      }
    }
  }

  .info-col {
    .info-tag {
      display: inline-block;
      font-size: 0.7rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: #7a7265;
      margin-bottom: 1rem;
    }

    .product-name {
      font-size: clamp(1.3rem, 2.2vw, 1.75rem);
      font-weight: 600;
      line-height: 1.3;
      color: #1c1c1c;
      margin: 0 0 2rem;
    }

    .info-table {
      border-top: 1px solid #d5d0c8;

      .info-row {
        display: grid;
        grid-template-columns: 110px 1fr;
        gap: 1rem;
        padding: 0.85rem 0;
        border-bottom: 1px solid #d5d0c8;
        align-items: center;

        .info-label {
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #7a7265;
          font-weight: 500;
        }

        .info-value {
          font-size: 0.95rem;
          color: #1c1c1c;

          &.mono {
            font-family: monospace;
            font-size: 0.85rem;
          }

          &.condition-badge {
            display: inline-block;
            background: var(--primary-color);
            color: #faf9f7;
            font-size: 0.7rem;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            padding: 0.25rem 0.7rem;
            border-radius: 4px;
          }
        }
      }
    }

    .quote-btn {
      margin-top: 2rem;
      width: 100%;
      background: var(--primary-color);
      color: #faf9f7;
      border: none;
      padding: 1rem;
      font-size: 0.85rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      cursor: pointer;
      transition: background 0.2s;
      font-weight: 600;

      &:hover {
        background: var(--secondary-color);
      }
    }

    .reassurance {
      margin-top: 1.2rem;
      font-size: 0.85rem;
      line-height: 1.5;
      color: #7a7265;
      font-style: italic;
      border-left: 2px solid #d5d0c8;
      padding-left: 1rem;
    }
  }

  .detail-section {
    max-width: 1200px;
    margin: 0 auto;
    border: 1px solid #d5d0c8;
    background: #fff;
  }

  .tab-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-bottom: 1px solid #d5d0c8;

    .tab-btn {
      background: transparent;
      border: none;
      border-right: 1px solid #d5d0c8;
      padding: 1.1rem 0.8rem;
      font-size: 0.75rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #7a7265;
      cursor: pointer;
      transition: all 0.2s;
      font-weight: 600;

      &:last-child {
        border-right: none;
      }

      &:hover {
        background: #f5f2ed;
        color: var(--primary-color);
      }

      &.active {
        background: var(--primary-color);
        color: #faf9f7;
      }
    }
  }

  .tab-panel {
    padding: 2.5rem;
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  .panel-tag {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  .feature-overview p {
    font-size: 1rem;
    line-height: 1.7;
    color: black;
    margin: 0;
  }

  .key-features ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: grid;
      grid-template-columns: 32px 1fr;
      gap: 1rem;
      padding: 0.85rem 0;
      border-bottom: 1px solid #ede8e0;
      font-size: 0.95rem;
      color: black;
      line-height: 1.5;

      &:last-child {
        border-bottom: none;
      }

      .feat-num {
        font-size: 0.7rem;
        letter-spacing: 0.05em;
        color: #b0a898;
        font-weight: 600;
        padding-top: 0.15rem;
      }
    }
  }

  .spec-block {
    border-left: 2px solid #d5d0c8;
    padding-left: 1.5rem;
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }

    .spec-title {
      font-size: 0.8rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #1c1c1c;
      font-weight: 600;
      margin: 0 0 0.8rem;
    }

    .spec-measurements {
      display: flex;
      flex-direction: column;
      gap: 0;

      .spec-row {
        padding: 0.5rem 0;
        border-bottom: 1px solid #ede8e0;
        font-size: 0.9rem;
        color: #3d3d3d;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }

  .included-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: grid;
      grid-template-columns: 28px 1fr;
      gap: 0.5rem;
      padding: 0.8rem 0;
      border-bottom: 1px solid #ede8e0;
      font-size: 0.95rem;
      color: #3d3d3d;

      &:last-child {
        border-bottom: none;
      }

      .check-mark {
        font-size: 0.9rem;
        color: #1c1c1c;
      }
    }
  }

  .policies-content {
    gap: 0;
  }

  .policy-block {
    padding: 2rem 0;
    border-bottom: 1px solid #d5d0c8;

    &:last-child {
      border-bottom: none;
    }

    .policy-heading {
      font-size: 0.8rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: #1c1c1c;
      font-weight: 600;
      margin: 0 0 1.2rem;
    }

    .policy-row {
      display: grid;
      grid-template-columns: 16px 1fr;
      gap: 0.8rem;
      margin-bottom: 0.75rem;
      align-items: baseline;

      &:last-child {
        margin-bottom: 0;
      }

      .policy-dot {
        width: 5px;
        height: 5px;
        background: #b0a898;
        border-radius: 50%;
        margin-top: 0.55rem;
        flex-shrink: 0;
      }

      p {
        font-size: 0.9rem;
        line-height: 1.6;
        color: #3d3d3d;
        margin: 0;
      }
    }

    .policy-subblock {
      margin: 1.2rem 0 0.5rem 1.5rem;

      .panel-tag {
        margin-bottom: 0.7rem;
      }
    }

    .inline-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;

      .policy-tag {
        font-size: 0.7rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        border: 1px solid #d5d0c8;
        padding: 0.3rem 0.7rem;
        color: #555;
        font-weight: 500;
      }
    }
  }

  /* Responsive */
  @media screen and (max-width: 1200px) {
    padding: 2rem 3rem 5rem;
    .top-section {
      gap: 3rem;
    }
  }

  @media screen and (max-width: 900px) {
    padding: 1.5rem 2rem 4rem;
    .top-section {
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1.2rem 1rem 3rem;
    .tab-bar {
      grid-template-columns: repeat(2, 1fr);
    }
    .tab-panel {
      padding: 1.5rem;
    }
    .info-col .info-table .info-row {
      grid-template-columns: 90px 1fr;
      gap: 0.5rem;
    }
  }

  @media screen and (max-width: 420px) {
    padding: 1rem 0.8rem 2.5rem;
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 600px) {
    width: 95%;
    border-radius: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 2px solid #e2e8f0;

  .modal-tag {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #7a7265;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  h3 {
    font-size: 1rem;
    font-weight: 500;
    margin: 0;
    line-height: 1.4;
    max-width: 330px;
    color: #1c1c1c;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: #666;
    transition: all 0.3s ease;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #e53e3e;
      transform: rotate(90deg);
    }
  }

  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;

  label {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #7a7265;
    font-weight: 600;
    margin-bottom: 0.5rem;

    span {
      color: #e53e3e;
    }
  }

  input,
  textarea {
    width: 100%;
    padding: 0.75rem;
    font-size: 0.9rem;
    border: 1px solid #d5d0c8;
    border-radius: 6px;
    transition: all 0.3s ease;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
    }

    &::placeholder {
      color: #b0a898;
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  @media (max-width: 600px) {
    margin-bottom: 1rem;
    padding: 0;
  }
`;

const SubmitButton = styled.button`
  width: calc(100% - 1rem);
  margin: 0 0.5rem;
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  &:hover {
    background: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 600px) {
    width: 100%;
    margin: 0;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;

  svg {
    font-size: 3rem;
    color: #38a169;
    margin-bottom: 1rem;
  }

  h4 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    color: #666;
  }

  @media (max-width: 600px) {
    padding: 2rem 1.5rem;

    svg {
      font-size: 2.5rem;
    }

    h4 {
      font-size: 1.1rem;
    }
  }
`;

// Lightbox Components
const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease;
`;

const LightboxContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxCloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: #e53e3e;
    transform: scale(1.05);
  }

  svg {
    font-size: 1.25rem;
  }
`;

const LightboxNavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: var(--primary-color);
    transform: translateY(-50%) scale(1.05);
  }

  &.prev {
    left: 1rem;
  }

  &.next {
    right: 1rem;
  }

  svg {
    font-size: 1.5rem;
  }

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;

    svg {
      font-size: 1.2rem;
    }
  }
`;

const LightboxImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    animation: zoomIn 0.3s ease;
  }

  @keyframes zoomIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const LightboxCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  backdrop-filter: blur(4px);
`;

export default ProductInfo;
